/**
 * Account storage on the web address binds to `dovod.account` once per page.
 * Account keys use `dovod:<userId>:<key>` in localStorage and sessionStorage;
 * account databases use `<name>:<userId>`. Device keys and the marker stay
 * unprefixed. A page without a marker keeps account data in memory.
 *
 * A load erases other accounts' namespaces. Reconciliation sets the token's
 * account as the marker and erases other namespaces; a different page binding
 * requires a reload because stores hydrate at import. Sign-out deletes the
 * bound account's namespace. Other tabs reload over a BroadcastChannel.
 * Stale pages keep their binding, so their writes remain in their own namespace
 * and the next load erases them. Other builds keep device-local storage.
 */

import { onWebAddress } from "./shared/auth/web-address";

/** The device's own settings, kept across accounts. Each owner reads its key from here. */
export const DEVICE_STORAGE_KEYS = {
  /** Also read by `public/theme-boot.js`, before the bundle loads. */
  theme: "t3code:theme",
  locale: "t3code:locale",
  deviceId: "dovod.device-id",
  threadSidebarWidth: "chat_thread_sidebar_width",
  documentsPanelWidth: "t3code:documents-panel-width:v2",
} as const;

export const ACCOUNT_MARKER_KEY = "dovod.account";
export const ACCOUNT_DATABASES: readonly string[] = ["t3code-voice-clips"];

const CHANNEL_NAME = "dovod.account";
const ACCOUNT_PREFIX = "dovod:";
const webAddress = onWebAddress();
const pageAccountId =
  webAddress && typeof window !== "undefined"
    ? window.localStorage.getItem(ACCOUNT_MARKER_KEY)
    : null;

const DEVICE_KEYS: ReadonlySet<string> = new Set([
  ...Object.values(DEVICE_STORAGE_KEYS),
  ACCOUNT_MARKER_KEY,
]);

export function isDeviceKey(key: string): boolean {
  return DEVICE_KEYS.has(key);
}

export type BrowserStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export function accountStorageKey(key: string): string | null {
  if (!webAddress || isDeviceKey(key)) return key;
  return pageAccountId === null ? null : `${ACCOUNT_PREFIX}${pageAccountId}:${key}`;
}

export function accountDatabaseName(name: string): string | null {
  if (!webAddress) return name;
  return pageAccountId === null ? null : `${name}:${pageAccountId}`;
}

function wrapStorage(browserStorage: () => Storage | undefined): BrowserStorage {
  const memory = new Map<string, string>();
  return {
    getItem: (key) => {
      const name = accountStorageKey(key);
      const storage = name === null ? undefined : browserStorage();
      return storage && name !== null ? storage.getItem(name) : (memory.get(key) ?? null);
    },
    setItem: (key, value) => {
      const name = accountStorageKey(key);
      const storage = name === null ? undefined : browserStorage();
      if (storage && name !== null) storage.setItem(name, value);
      else memory.set(key, value);
    },
    removeItem: (key) => {
      const name = accountStorageKey(key);
      const storage = name === null ? undefined : browserStorage();
      if (storage && name !== null) storage.removeItem(name);
      else memory.delete(key);
    },
  };
}

export const accountLocalStorage = wrapStorage(() =>
  typeof window === "undefined" ? undefined : window.localStorage,
);
export const accountSessionStorage = wrapStorage(() =>
  typeof window === "undefined" ? undefined : window.sessionStorage,
);

export interface AccountStorage {
  readonly accountId: string | null;
  readonly local: Storage;
  readonly session: Storage;
  readonly listDatabases: () => Promise<readonly string[]>;
  readonly deleteDatabases: (names: readonly string[]) => Promise<void>;
  readonly announce: () => void;
}

function storageKeys(storage: Storage): string[] {
  return Array.from({ length: storage.length }, (_, index) => storage.key(index)).filter(
    (key) => key !== null,
  );
}

async function eraseOtherAccounts(storage: AccountStorage, userId: string | null): Promise<void> {
  const keepPrefix = userId === null ? null : `${ACCOUNT_PREFIX}${userId}:`;
  for (const target of [storage.local, storage.session]) {
    for (const key of storageKeys(target)) {
      if (key.startsWith(ACCOUNT_PREFIX) && (keepPrefix === null || !key.startsWith(keepPrefix))) {
        target.removeItem(key);
      }
    }
  }
  const databases = await storage.listDatabases();
  if (storage.local.getItem(ACCOUNT_MARKER_KEY) !== userId) return;
  await storage.deleteDatabases(
    databases.filter((name) =>
      ACCOUNT_DATABASES.some(
        (base) => name.startsWith(`${base}:`) && (userId === null || name !== `${base}:${userId}`),
      ),
    ),
  );
}

export function reconcileAccount(storage: AccountStorage, userId: string) {
  const changed = storage.local.getItem(ACCOUNT_MARKER_KEY) !== userId;
  storage.local.setItem(ACCOUNT_MARKER_KEY, userId);
  const erasing = eraseOtherAccounts(storage, userId);
  if (changed) storage.announce();
  return {
    outcome: storage.accountId === userId ? ("kept" as const) : ("reload" as const),
    cleanup: erasing,
  };
}

export async function forgetAccount(storage: AccountStorage): Promise<void> {
  if (storage.accountId !== null) {
    const prefix = `${ACCOUNT_PREFIX}${storage.accountId}:`;
    for (const target of [storage.local, storage.session]) {
      for (const key of storageKeys(target)) {
        if (key.startsWith(prefix)) target.removeItem(key);
      }
    }
  }
  if (storage.local.getItem(ACCOUNT_MARKER_KEY) === storage.accountId) {
    storage.local.removeItem(ACCOUNT_MARKER_KEY);
  }
  storage.announce();
  await storage.deleteDatabases(
    storage.accountId === null
      ? []
      : ACCOUNT_DATABASES.map((base) => `${base}:${storage.accountId}`),
  );
}

let channel: BroadcastChannel | null | undefined;

// A shared channel instance keeps an account announcement from reloading its sender.
function accountChannel(): BroadcastChannel | null {
  channel ??= typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(CHANNEL_NAME);
  return channel;
}

/** Calls `onChange` when another tab changes the origin's account. */
export function watchAccountChanges(onChange: () => void): () => void {
  const opened = accountChannel();
  if (opened === null) return () => undefined;
  const listener = () => onChange();
  opened.addEventListener("message", listener);
  return () => opened.removeEventListener("message", listener);
}

const DATABASE_DELETE_TIMEOUT_MS = 3_000;

async function deleteIndexedDbDatabases(names: readonly string[]): Promise<void> {
  if (typeof indexedDB === "undefined" || names.length === 0) return;
  const timeout = Promise.withResolvers<void>();
  const timer = setTimeout(timeout.resolve, DATABASE_DELETE_TIMEOUT_MS);
  try {
    await Promise.race([
      Promise.all(
        names.map((name) => {
          const done = Promise.withResolvers<void>();
          const request = indexedDB.deleteDatabase(name);
          request.addEventListener("success", () => done.resolve());
          request.addEventListener("error", () => done.resolve());
          return done.promise;
        }),
      ),
      timeout.promise,
    ]);
  } finally {
    clearTimeout(timer);
  }
}

export function browserAccountStorage(): AccountStorage {
  return {
    accountId: pageAccountId,
    local: window.localStorage,
    session: window.sessionStorage,
    listDatabases: async () => {
      if (typeof indexedDB === "undefined" || typeof indexedDB.databases !== "function") return [];
      return indexedDB.databases().then(
        (databases) => databases.flatMap((database) => (database.name ? [database.name] : [])),
        () => [],
      );
    },
    deleteDatabases: deleteIndexedDbDatabases,
    announce: () => accountChannel()?.postMessage("changed"),
  };
}

export const accountStorageReady =
  webAddress && typeof window !== "undefined"
    ? eraseOtherAccounts(browserAccountStorage(), pageAccountId)
    : Promise.resolve();
